import {useEffect, useRef, useState} from 'react';
import * as powerbi from 'powerbi-client';
import { useAxiosAuthenticated } from '../../../useAxiosAuthenticated';

const PROD_POWER_BI_IDS = {
    reportId: "4d5dd1b5-1d57-4ec4-b413-ca56870d6b67",
    datasetId: "9955ee21-b0aa-416c-b0a7-bb61245c8ae8",
};

const QA_POWER_BI_IDS = {
    reportId: "01fe08c9-181a-44a8-8039-70ea282312f8",
    datasetId: "c926891f-417f-4757-9621-a67104023f1b",
};

const resolvePowerBiIds = (apiBaseUrl) => {
    if (!apiBaseUrl) {
        return QA_POWER_BI_IDS;
    }

    try {
        const { hostname } = new URL(apiBaseUrl);
        return hostname === "api.igrad.com" ? PROD_POWER_BI_IDS : QA_POWER_BI_IDS;
    } catch (error) {
        console.warn("Failed to parse REACT_APP_API_BASE_URL, defaulting to QA IDs.", error);
        return QA_POWER_BI_IDS;
    }
};

const { reportId: REPORT_ID, datasetId: DATASET_ID, workspaceId: WORKSPACE_ID } = resolvePowerBiIds(process.env.REACT_APP_API_BASE_URL);

const FinancialWellnessChange = () => {
    const containerRef = useRef(null);
    const { post } = useAxiosAuthenticated();
    const powerbiServiceRef = useRef(null);

    const [embedConfig, setEmbedConfig] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [errorText, setErrorText] = useState("");

    useEffect(() => {
        if (!powerbiServiceRef.current) {
            powerbiServiceRef.current = new powerbi.service.Service(
                powerbi.factories.hpmFactory,
                powerbi.factories.wpmpFactory,
                powerbi.factories.routerFactory
            );
        }

        return () => {
            if (containerRef.current && powerbiServiceRef.current) {
                powerbiServiceRef.current.reset(containerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const fetchEmbedConfig = async () => {
            try {
                const baseUrl = process.env.REACT_APP_API_BASE_URL;
                if (!baseUrl) {
                    throw new Error("Missing API base URL configuration.");
                }

                const requestUrl = `${baseUrl.replace(/\/$/, "")}/powerbi/embed-config`;
                const response = await post(requestUrl, {                    
                    reportId: REPORT_ID,
                    datasetId: DATASET_ID,
                    workspaceId: WORKSPACE_ID
                });

                const data = response.data ?? {};

                if (!data.embedToken || !data.embedUrl) {
                    throw new Error("Incomplete embed configuration returned from API.");
                }

                setEmbedConfig({
                    accessToken: data.embedToken,
                    embedUrl: data.embedUrl,
                    reportId: data.reportId ?? REPORT_ID,
                });
                setErrorText("");
            } catch (error) {
                console.error("Error fetching Power BI embed configuration:", error);
                setErrorText("Failed to load the Financial Wellness Change report. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchEmbedConfig();
    }, [post]);

    useEffect(() => {
        if(
            !embedConfig?.accessToken ||
            !containerRef.current ||
            !powerbiServiceRef.current
        ) {
            return;
        }

        powerbiServiceRef.current.reset(containerRef.current);

        powerbiServiceRef.current.embed(containerRef.current, {
            type: 'report',
            tokenType: powerbi.models.TokenType.Embed,
            accessToken: embedConfig.accessToken,
            embedUrl: embedConfig.embedUrl,
            id: embedConfig.reportId,
            settings: {
                panes: {
                    filters: { visible: false },
                    pageNavigation: { visible: true }
                },
                bars: {
                    statusBar: { visible: true }
                },
            },
        });

        return() => {
            if (containerRef.current) {
                powerbiServiceRef.current.reset(containerRef.current);
            }
        }
    }, [embedConfig]);

    if (isLoading) {
        return <div>Loading Financial Wellness Change report...</div>;
    }

    if (errorText) {
        return <div className="text-red-600">{errorText}</div>;
    }

    return (
        <div className="w-full h-[80vh]">
            <div 
                ref={containerRef} 
                style={{ height: '100%', width: '100%' }} 
                role="region" 
                aria-label='Financial Wellness Change Fabric Report'
            />
        </div>
    );
};

export default FinancialWellnessChange;